import {
    Box,
    Button,
    ButtonRow,
    Checkbox,
    Form,
    FormGroup,
    FormGroupContext,
    IconButton,
    List,
    Modal,
    SuccessAnimation,
    Text,
    TextInput,
} from '@ksyos/design-system';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { AddEdit } from '@common/utils/utilityTypes';
import type {
    ICarePathInformationRequestValidated,
    IInformationRequestCustomControlDataValidated,
} from '@common/validation/carePathInformationRequest';
import type { IReduxState } from '@frontend/configureStore';
import { EpisodeCategory } from '@common/enums';
import { carePathInformationRequestSchema } from '@common/validation/carePathInformationRequest';
import { translateKey } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';

interface ICarePathInformationRequestModalProps {
    onClose: () => void;
    selectedCarePath: EpisodeCategory;
}

const selectableCarePaths = [
    EpisodeCategory.somnology,
    EpisodeCategory.dermatology,
    EpisodeCategory.mentalHealth,
    EpisodeCategory.ophthalmology,
    EpisodeCategory.cardiology,
] as const;

type AddEditCustomControlData = Omit<AddEdit<IInformationRequestCustomControlDataValidated>, 'carepaths'> & {
    carepaths: IInformationRequestCustomControlDataValidated['carepaths'];
};

type AddEditCarePathInformationRequest = Omit<ICarePathInformationRequestValidated, 'customControlData'> & {
    customControlData: AddEditCustomControlData;
};

export function CarePathInformationRequestModal(props: ICarePathInformationRequestModalProps) {
    const { onClose, selectedCarePath } = props;
    const abortableFetch = useAbortableFetch();
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const [carePathInformationRequest, setCarePathInformationRequest] = useState<AddEditCarePathInformationRequest>({
        customControlData: {
            availability: undefined,
            carepaths: {
                cardiology: false,
                dermatology: false,
                mentalHealth: false,
                ophthalmology: false,
                somnology: false,
            },
            emailadres: null,
            phonenumber: undefined,
        },
    });
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [wantsToBeCalled, setWantsTobeCalled] = useState(false);
    const [isSaveModalButtonEnabled, setIsSaveModalButtonEnabled] = useState(true);
    const [isVideoPlayerActive, setIsVideoPlayerActive] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const { image, taglines, title, video, captions } = getCarePathSpecificContent(selectedCarePath);

    useEffect(() => {
        initialize();
    }, []);

    return (
        <Modal
            isOpen={true}
            onClose={() => onClose()}
            size="large"
        >
            <FormGroupContext.Provider value={{ layout: 'vertical', size: 'large', variant: 'outline' }}>
                {!showSuccess && (
                    <>
                        <Modal.Header>{title}</Modal.Header>
                        <Modal.CloseButton />
                        <Modal.Body>
                            {isVideoPlayerActive ? (
                                <Box mb={8}>
                                    <ButtonRow justify="right">
                                        <IconButton
                                            icon="close"
                                            labelText="Close"
                                            onClick={() => setIsVideoPlayerActive(false)}
                                            size="small"
                                        />
                                    </ButtonRow>
                                    <video
                                        autoPlay
                                        controls
                                        height="auto"
                                        width="100%"
                                    >
                                        <source
                                            src={video}
                                            type="video/mp4"
                                        />
                                        <track
                                            kind="captions"
                                            src={captions.src}
                                            srcLang={captions.srcLang}
                                        />
                                    </video>
                                </Box>
                            ) : (
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    flexDirection={['column-reverse', null, 'row']}
                                    gap={8}
                                    justifyContent="space-evenly"
                                    mb={6}
                                >
                                    <List sx={{ maxWidth: '344px' }}>
                                        {taglines.map((tagline, index) => {
                                            return (
                                                <List.Item
                                                    key={`tagline${index}`}
                                                    sx={{ alignItems: 'center', display: 'flex', flexDirection: 'row' }}
                                                >
                                                    <Box
                                                        as="span"
                                                        display="inline-block"
                                                        flexShrink={0}
                                                        mr={4}
                                                    >
                                                        <svg
                                                            fill="none"
                                                            height="24"
                                                            viewBox="0 0 25 25"
                                                            width="24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M12.3887 23.0682C18.4638 23.0682 23.3887 18.1434 23.3887 12.0682C23.3887 5.9931 18.4638 1.06824 12.3887 1.06824C6.31354 1.06824 1.38867 5.9931 1.38867 12.0682C1.38867 18.1434 6.31354 23.0682 12.3887 23.0682Z"
                                                                fill="url(#paint0_linear_167_37529)"
                                                            />
                                                            <path
                                                                d="M10.3887 17.0683C10.1235 17.0682 9.86921 16.9628 9.68171 16.7753L6.68171 13.7753C6.49956 13.5867 6.39876 13.3341 6.40104 13.0719C6.40332 12.8097 6.50849 12.5589 6.69389 12.3735C6.8793 12.1881 7.13011 12.0829 7.39231 12.0806C7.65451 12.0783 7.90711 12.1791 8.09571 12.3613L10.3887 14.6543L16.6817 8.36128C16.8703 8.17912 17.1229 8.07833 17.3851 8.0806C17.6473 8.08288 17.8981 8.18805 18.0835 8.37346C18.2689 8.55887 18.3741 8.80968 18.3764 9.07188C18.3787 9.33407 18.2779 9.58668 18.0957 9.77528L11.0957 16.7753C10.9082 16.9628 10.6539 17.0682 10.3887 17.0683Z"
                                                                fill="white"
                                                            />
                                                            <defs>
                                                                <linearGradient
                                                                    gradientUnits="userSpaceOnUse"
                                                                    id="paint0_linear_167_37529"
                                                                    x1="12.3887"
                                                                    x2="12.3887"
                                                                    y1="1.02524"
                                                                    y2="23.0252"
                                                                >
                                                                    <stop stopColor="#D7E1FB" />
                                                                    <stop
                                                                        offset="1"
                                                                        stopColor="#A6BAF1"
                                                                    />
                                                                </linearGradient>
                                                            </defs>
                                                        </svg>
                                                    </Box>
                                                    {tagline}
                                                </List.Item>
                                            );
                                        })}
                                    </List>
                                    <button
                                        onClick={() => setIsVideoPlayerActive(true)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            flexShrink: 0,
                                            padding: 0,
                                        }}
                                    >
                                        <img
                                            alt={image}
                                            height="219px"
                                            src={`${image}@1x.png`}
                                            srcSet={`${image}@1x.png 1x, ${image}@2x.png 2x, ${image}@3x.png 3x`}
                                            style={{
                                                cursor: 'pointer',
                                                height: 'auto',
                                                maxWidth: '100%',
                                                pointerEvents: 'none',
                                                width: 'auto',
                                            }}
                                            width="344px"
                                        />
                                    </button>
                                </Box>
                            )}
                            <Form submitOnEnter={false}>
                                <FormGroup
                                    hasErrors={errors.has('customControlData.emailadres')}
                                    isRequired={true}
                                    label={translateKey('carePathInformationRequest.receiveInformation')}
                                >
                                    <TextInput
                                        error={getValidationErrorTranslation(
                                            errors.get('customControlData.emailadres')
                                        )}
                                        onBlur={(e) => validateOne('emailadres', e.target.value)}
                                        onChange={(e) => handleChange('emailadres', e.target.value)}
                                        type="email"
                                        value={carePathInformationRequest.customControlData.emailadres ?? ''}
                                    />
                                </FormGroup>
                                <FormGroup
                                    hasErrors={errors.has('customControlData.interestedInCarePath')}
                                    label={translateKey(
                                        'carePathInformationRequest.interestedInCarePath',
                                        translateKey(`episodeCategoryEnum.${selectedCarePath}`)
                                    )}
                                    sx={{ mt: 8 }}
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                    >
                                        {selectableCarePaths
                                            .filter((carePath) => carePath !== selectedCarePath)
                                            .map((carePath) => (
                                                <React.Fragment key={carePath}>
                                                    <Checkbox
                                                        checked={
                                                            carePathInformationRequest.customControlData.carepaths[
                                                                carePath
                                                            ]
                                                        }
                                                        label={
                                                            <Text as="span">
                                                                {translateKey(`episodeCategoryEnum.${carePath}`)}
                                                            </Text>
                                                        }
                                                        onBlur={(e) =>
                                                            validateOne('carepaths', {
                                                                ...carePathInformationRequest.customControlData
                                                                    .carepaths,
                                                                [carePath]: e.target.checked,
                                                            })
                                                        }
                                                        onChange={(e) =>
                                                            handleChange('carepaths', {
                                                                ...carePathInformationRequest.customControlData
                                                                    .carepaths,
                                                                [carePath]: e.target.checked,
                                                            })
                                                        }
                                                    />
                                                </React.Fragment>
                                            ))}
                                    </Box>
                                </FormGroup>
                                <Checkbox
                                    checked={wantsToBeCalled}
                                    label={
                                        <Text as="span">
                                            {translateKey('carePathInformationRequest.wantsToBeCalled')}
                                        </Text>
                                    }
                                    onChange={() => setWantsTobeCalled(!wantsToBeCalled)}
                                    sx={{ mb: wantsToBeCalled ? 4 : 0, mt: 8 }}
                                />
                                {wantsToBeCalled && (
                                    <>
                                        <FormGroup
                                            hasErrors={errors.has('customControlData.receiveInformation')}
                                            isRequired={true}
                                            label={translateKey('common.phoneNumber')}
                                        >
                                            <TextInput
                                                error={getValidationErrorTranslation(
                                                    errors.get('customControlData.phonenumber')
                                                )}
                                                onBlur={(e) => validateOne('phonenumber', e.target.value)}
                                                onChange={(e) => handleChange('phonenumber', e.target.value)}
                                                value={carePathInformationRequest.customControlData.phonenumber ?? ''}
                                            />
                                        </FormGroup>
                                        <FormGroup
                                            hasErrors={errors.has('customControlData.availability')}
                                            label={translateKey('carePathInformationRequest.availableOn')}
                                        >
                                            <TextInput
                                                error={getValidationErrorTranslation(
                                                    errors.get('customControlData.availability')
                                                )}
                                                onBlur={(e) => validateOne('availability', e.target.value)}
                                                onChange={(e) => handleChange('availability', e.target.value)}
                                                value={carePathInformationRequest.customControlData.availability ?? ''}
                                            />
                                        </FormGroup>
                                    </>
                                )}
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={() => onClose()}
                                variant="outline"
                            >
                                {translateKey('common.cancel')}
                            </Button>
                            <Button
                                isDisabled={!isSaveModalButtonEnabled}
                                onClick={async () => await save()}
                                variant="solid"
                            >
                                {translateKey('common.send')}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
                {showSuccess && (
                    <>
                        <Modal.Body>
                            <Box
                                alignItems="center"
                                display="flex"
                                flexDirection="column"
                                mt={4}
                            >
                                <SuccessAnimation size="large" />
                                <Text>
                                    {' '}
                                    {translateKey('carePathInformationRequest.thanks', getAllCarePathsText())}.
                                </Text>
                                <Text>{translateKey('carePathInformationRequest.willReceiveEmail')}.</Text>
                            </Box>
                        </Modal.Body>
                        <Modal.Footer justify="center">
                            <Button
                                onClick={() => {
                                    onClose();
                                }}
                            >
                                {translateKey('common.close')}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </FormGroupContext.Provider>
        </Modal>
    );

    async function initialize() {
        const result = await abortableFetch.fetchJson(`/api/practitioners/${practitionerId}`, 'GET');
        setCarePathInformationRequest({
            ...carePathInformationRequest,
            customControlData: {
                ...carePathInformationRequest.customControlData,
                carepaths: {
                    ...carePathInformationRequest.customControlData.carepaths,
                    [selectedCarePath]: true,
                },
                emailadres: result.json?.emailAddress,
                phonenumber: result.json?.mobileNumber,
            },
        });
    }

    function handleChange<T extends keyof AddEditCustomControlData>(name: T, value: AddEditCustomControlData[T]) {
        setCarePathInformationRequest(getUpdatedCarePathInformationRequest(name, value));
    }

    function validateOne<T extends keyof AddEditCustomControlData>(name: T, value: AddEditCustomControlData[T]) {
        setErrors(
            updateValidationErrorsForOne(
                carePathInformationRequestSchema,
                getUpdatedCarePathInformationRequest(name, value),
                errors,
                `customControlData.${name}`
            )
        );
    }

    async function save() {
        setIsSaveModalButtonEnabled(false);
        const carePathInformationRequestToSave = carePathInformationRequest;
        if (!wantsToBeCalled) {
            delete carePathInformationRequestToSave.customControlData.phonenumber;
            delete carePathInformationRequestToSave.customControlData.availability;
        }

        const { errors: validatedErrors, item } = validate(
            carePathInformationRequestSchema,
            carePathInformationRequestToSave
        );

        if (item && errors.size === 0) {
            await abortableFetch.postJson('/api/carePathInformationRequests', item);
            setShowSuccess(true);
        } else {
            setErrors(validatedErrors);
        }
        setIsSaveModalButtonEnabled(true);
    }

    function getCarePathSpecificContent(type: EpisodeCategory) {
        const mapping = {
            [EpisodeCategory.somnology]: {
                captions: { src: 'assets/videos/slaap-nl.vtt', srcLang: 'nl' },
                image: 'assets/images/video-thumbnail-somnology',
                taglines: [
                    'Geen wachttijd, direct starten met een slaaponderzoek in eigen bed',
                    'Ksyos begeleidt patiënt vanaf verwijzing tot en met behandeling en daarna',
                ],
                title: 'Slaaponderzoek bij de patiënt thuis ',
                video: 'assets/videos/slaap.mp4',
            },
            [EpisodeCategory.cardiology]: {
                captions: { src: 'assets/videos/thuisholter-nl.vtt', srcLang: 'nl' },
                image: 'assets/images/video-thumbnail-cardiology',
                taglines: [
                    'Binnen een week een onderzoek',
                    'Kosteloos gebruik van de Ksyos apparatuur',
                    'Beoordeling door regionale cardioloog',
                ],
                title: 'Cardiovasculaire klachten eenvoudig diagnosticeren met de holter of rust-ECG',
                video: 'assets/videos/thuisholter.mp4',
            },
            [EpisodeCategory.ophthalmology]: {
                captions: { src: 'assets/videos/oogheelkunde-nl.vtt', srcLang: 'nl' },
                image: 'assets/images/video-thumbnail-ophthalmology',
                taglines: [
                    'Binnen een week een oogonderzoek',
                    'Direct een diagnose en behandeladvies',
                    'Eenvoudig verwijzen via Zorgdomein',
                ],
                title: 'Snel oogklachten diagnosticeren door samenwerking tussen oogarts, optometrist en orthoptist',
                video: 'assets/videos/oogheelkunde.mp4',
            },
            [EpisodeCategory.mentalHealth]: {
                captions: { src: 'assets/videos/ggz-nl.vtt', srcLang: 'nl' },
                image: 'assets/images/video-thumbnail-mental-health',
                taglines: [
                    'Snelle consultatie van GGZ specialist',
                    'Ondersteuning voor POH-GGZ',
                    'Gemakkelijke monitoring van patiënten',
                ],
                title: 'Een totaalpakket voor GGZ ondersteuning in de huisartsenpraktijk',
                video: 'assets/videos/ggz.mp4',
            },
            [EpisodeCategory.dermatology]: {
                captions: { src: 'assets/videos/dermatologie-thuisconsult-nl.vtt', srcLang: 'nl' },
                image: 'assets/images/video-thumbnail-dermatology',
                taglines: ['Binnen drie dagen een diagnose', 'Contact met dermatoloog uit de regio', 'Geen wachttijd'],
                title: 'Snel en eenvoudig huidklachten diagnosticeren vanuit de huisartspraktijk',
                video: 'assets/videos/dermatologie-thuisconsult.mp4',
            },
        };

        return mapping[type as keyof typeof mapping];
    }

    function getUpdatedCarePathInformationRequest<T extends keyof AddEditCustomControlData>(
        name: T,
        value: AddEditCustomControlData[T]
    ) {
        return {
            ...carePathInformationRequest,
            customControlData: { ...carePathInformationRequest.customControlData, [name]: value },
        };
    }

    function getAllCarePathsText() {
        const selectedCarePaths = Object.keys(carePathInformationRequest.customControlData.carepaths).filter(
            (key) =>
                !!carePathInformationRequest.customControlData.carepaths[
                    key as keyof AddEditCarePathInformationRequest['customControlData']['carepaths']
                ]
        );

        const translatedCarePaths = selectedCarePaths.map((carePath) =>
            translateKey(`episodeCategoryEnum.${carePath}`)
        );
        if (selectedCarePaths.length > 1) {
            const lastCarePath = translatedCarePaths.pop();

            return `${translatedCarePaths.join(', ')} ${translateKey('common.and')} ${lastCarePath}`;
        }

        return translatedCarePaths[0];
    }
}
